<template>
  <div
    v-if="
      tryForFree ||
      isSubscribed ||
      selectYearly === subscription.billingFrequency
    "
    class="p-5-content glass text-base-content"
  >
    <div
      v-if="showYearlySelect && subscriptionType !== 1"
      class="flex items-center gap-2 justify-center mb-4"
    >
      <p>{{ $t(`purchase.sub.payByMonth`) }}</p>
      <VanSwitch
        :active-value="2"
        :inactive-value="1"
        :model-value="selectYearly"
        :size="18"
        @update:model-value="updateIsYearly"
      />
      <p>{{ $t(`purchase.sub.payByYear`) }}</p>
    </div>

    <!-- Title / Popular -->
    <div class="flex justify-between">
      <h1 class="text-xl sm:text-2xl font-semibold">
        {{ $t(`purchase.sub.${subscriptionType}.title`) }}
      </h1>
      <template v-if="!tryForFree">
        <template v-if="!isSubscribed">
          <div
            v-if="subscriptionType === 3"
            class="text-important flex gap-2 items-center border border-important px-3 rounded-xl"
          >
            <i class="iconfont icon-star"></i>
            <span>{{ $t(`purchase.sub.popular`) }}</span>
          </div>
        </template>

        <span v-else class="text-base-content/50">
          {{
            isYearly === 2
              ? `(${t(`purchase.sub.payByYear`)})`
              : `(${t(`purchase.sub.payByMonth`)})`
          }}
        </span>
      </template>
      <div
        v-else
        class="text-important flex gap-2 items-center border border-important px-3 rounded-xl"
      >
        <i class="iconify ph--coffee-fill"></i>
        <span>{{ $t(`purchase.sub.trial`) }}</span>
      </div>
    </div>

    <!-- Price & Save -->
    <p class="mt-1.5 mb-2.5 leading-[40px]">
      <template v-if="hasTry">
        <span class="text-[40px]">$0.00</span>
        <span class="text-[20px] line-through">
          ${{ isYearly === 2 ? yearPrice : monthPrice }}
        </span>
        <span class="font-serif text-[20px]">
          /{{ $t(`purchase.sub.month`) }}
        </span>
      </template>
      <template v-else-if="!isSubscribed">
        <span class="text-[40px]">
          ${{ isYearly === 2 ? yearPrice : monthPrice }}
        </span>
        <span class="font-serif text-[20px]">
          /{{ $t(`purchase.sub.month`) }}
        </span>
      </template>
    </p>
    <div
      v-if="isYearly === 2 && yearPrice > 0 && !hasTry"
      class="flex justify-between mt-1.5 mb-2.5"
    >
      <div>
        <span>
          ${{ Number((yearPrice * 12).toFixed(2)) }}/
          {{ $t(`purchase.sub.year`).toLowerCase() }}
        </span>
        <span class="line-through text-xs">
          ${{ (monthPrice * 12).toFixed(2) }}/
          {{ $t(`purchase.sub.year`).toLowerCase() }}
        </span>
      </div>
      <div>
        <span class="text-xs text-important">
          {{ $t(`purchase.sub.save`) }}
          ${{ Number(((monthPrice - yearPrice) * 12).toFixed(2)) }}
        </span>
      </div>
    </div>

    <!-- Action -->
    <button
      v-if="isSubscribed"
      class="btn btn-primary glass bg-[#e52d27] w-full rounded-sm"
      @click="clickManage"
    >
      {{ $t(`Buttons.manage`) }}
    </button>

    <button
      v-else-if="
        userStore.user.subscriptionType === 1 && subscriptionType === 1
      "
      class="btn btn-primary glass bg-[#e52d27] w-full rounded-sm"
      disabled
    >
      {{ $t(`purchase.sub.current`) }}
    </button>

    <template v-else-if="hasTry">
      <div class="flex gap-1">
        <!-- <button
          class="btn glass bg-neutral rounded-sm"
          @click="clickSubscription"
        >
          {{ $t(`purchase.sub.subscribe`) }}
        </button> -->

        <div class="indicator flex-1" @click="clickTryForFree">
          <!-- <span class="indicator-item badge badge-private right-[16px]"> </span> -->
          <button
            class="btn btn-primary glass bg-[#e52d27] btn-md rounded-sm w-full flex items-center"
          >
            <span class="text-xl"> {{ $t(`purchase.sub.tryFree`) }} </span>
            <span class="text-xs translate-y-0.5 opacity-70">
              ({{ $t(`purchase.sub.free3days`) }})
            </span>
          </button>
        </div>
      </div>
    </template>
    <button
      v-else
      class="btn btn-primary glass bg-[#e52d27] w-full rounded-sm text-xl"
      @click="clickSubscription"
    >
      {{ $t(`purchase.sub.subscribe`) }}
    </button>

    <p v-if="subscription.dtEnd" class="text-end text-xs mt-1 opacity-60">
      {{
        $t(`purchase.sub.expireDate`, {
          date: getDisplayDateTime(subscription.dtEnd),
        })
      }}
    </p>

    <p v-if="tryForFree" class="text-end text-xs mt-1 opacity-60">
      {{
        $t(`purchase.sub.expireDate`, {
          date: getDisplayDateTime(userStore.user.dtSubscriptionTrialEnd),
        })
      }}
    </p>

    <ul class="flex flex-col gap-2 mt-6">
      <li
        v-for="(_, idx) in profitsLength[subscriptionType]"
        class="flex gap-3"
      >
        <check-circle-fill-icon class="text-primary icon-xl rounded-full" />
        <span>
          {{ $t(`purchase.sub.${subscriptionType}.profits[${idx}]`) }}
        </span>
      </li>

      <template v-if="banLength[subscriptionType]">
        <li
          v-for="(_, idx) in banLength[subscriptionType]"
          class="flex gap-3 items-center"
        >
          <i
            class="iconfont icon-xmark text-primary rounded-full w-5 flex-shrink-0 h-5 text-center leading-5 text-xs light:text-white"
          />
          <span class="text-my-gray">
            {{ $t(`purchase.sub.${subscriptionType}.ban[${idx}]`) }}
          </span>
        </li>
      </template>

      <template v-if="starLength[subscriptionType]">
        <li
          v-for="(_, idx) in starLength[subscriptionType]"
          class="flex gap-3 items-center text-important"
        >
          <i
            class="iconfont icon-star w-5 flex-shrink-0 h-5 text-center leading-5 text-xs"
          />
          <span>
            {{ $t(`purchase.sub.${subscriptionType}.star[${idx}]`) }}
          </span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import {
  ApiPostSubscription,
  ApiGetStripeBillingPortal,
  ApiPostSubscriptionTrial,
} from "@/apis/payment";
import { getDisplayDateTime } from "@/modules/common";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { showLoadingToast } from "vant";
import { computed } from "vue";
import { PropType } from "vue";
import { ref } from "vue";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  subscription: {
    type: Object as PropType<Subscription>,
    required: true,
  },
  selectYearly: {
    type: Number,
    default: 2,
  },
  showYearlySelect: {
    type: Boolean,
    default: false,
  },
  tryForFree: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:selectYearly"]);

const monthPrice = ref(0);
const yearPrice = ref(0);
const userStore = useGlobalUser();
const modalStore = useGlobalModal();

const subscriptionType = computed(() => props.subscription.subscriptionType);

const isYearly = computed(
  () => props.subscription.billingFrequency || props.selectYearly
);

const isCurrent = computed(
  () => userStore.user.subscriptionType === subscriptionType.value
);

const hasTry = computed(
  () =>
    userStore.user.subscriptionType <= 2 &&
    userStore.user.dtSubscriptionTrialEnd === null &&
    subscriptionType.value === 3
);

const isSubscribed = computed(() =>
  userStore.subscriptions.some(
    (sub) =>
      sub.subscriptionType === subscriptionType.value &&
      sub.billingFrequency === isYearly.value
  )
);

const profitsLength: any = {
  1: 4,
  2: 6,
  3: 7,
  4: 3,
};

const banLength: any = {
  1: 3,
  2: 2,
  3: 0,
  4: 0,
};

const starLength: any = {
  1: 0,
  2: 0,
  3: 0,
  4: 3,
};

const clickManage = async () => {
  showLoadingToast(t(`Message.loading`));
  const { data } = await ApiGetStripeBillingPortal();
  location.href = data.redirectUrl;
};

const clickSubscription = () => {
  ApiPostSubscription({
    subscriptionType: subscriptionType.value,
    billingFrequency: props.selectYearly,
  });
};

const clickTryForFree = async () => {
  const loading = showLoadingToast(t(`Message.loading`));
  await ApiPostSubscriptionTrial();
  loading.close();
  modalStore.showSubscribeModal = false;
  modalStore.openReceivedModal([], "Subscription");
};

const updateIsYearly = (val: number) => {
  emit("update:selectYearly", val);
};

onMounted(() => {
  monthPrice.value =
    Number(t(`purchase.sub.${subscriptionType.value}.priceMonth`)) || 0;
  yearPrice.value =
    Number(t(`purchase.sub.${subscriptionType.value}.priceMonthYearly`)) || 0;
});
</script>
